/**
 * BracketEditorModal - Full bracket editor for managing all matchups
 * Allows setting teams, dates, times, locations, and broadcast info for all games
 */
import { useState, useEffect } from "react";
import { X, Loader2, Calendar, Clock, MapPin, Tv, Users, ChevronDown, ChevronUp, Save } from "lucide-react";
import { Button } from "../../ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const getAuthHeader = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const BracketEditorModal = ({ playoff, teams, leagueId, onClose, onSave }) => {
  const [matchups, setMatchups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expandedRounds, setExpandedRounds] = useState({});

  useEffect(() => {
    // Initialize matchups from playoff data
    const initialMatchups = (playoff.matchups || []).map(m => ({
      ...m,
      game_date: m.game_date || "",
      game_time: m.game_time || "",
      location: m.location || "",
      tv_network: m.tv_network || "",
      youtube_url: m.youtube_url || "",
      team1_id: m.team1_id || "",
      team2_id: m.team2_id || "",
      team1_score: m.team1_score ?? "",
      team2_score: m.team2_score ?? ""
    }));
    setMatchups(initialMatchups);
    
    // Expand first round by default
    const rounds = [...new Set(initialMatchups.map(m => m.round))];
    const expanded = {};
    rounds.forEach(r => expanded[r] = true);
    setExpandedRounds(expanded);
  }, [playoff]);

  const updateMatchup = (matchupId, field, value) => {
    setMatchups(prev => prev.map(m => 
      m.id === matchupId ? { ...m, [field]: value } : m
    ));
  };

  const getTeamName = (teamId) => {
    if (!teamId) return "TBD";
    const team = teams.find(t => t.id === teamId);
    return team?.name || "TBD";
  };

  const getTeamAbbr = (teamId) => {
    if (!teamId) return "TBD";
    const team = teams.find(t => t.id === teamId);
    return team?.abbreviation || team?.name?.substring(0, 3) || "TBD";
  };

  // Group matchups by round
  const matchupsByRound = matchups.reduce((acc, m) => {
    const round = m.round || 1;
    if (!acc[round]) acc[round] = [];
    acc[round].push(m);
    return acc;
  }, {});

  const rounds = Object.keys(matchupsByRound).sort((a, b) => parseInt(a) - parseInt(b));

  const getRoundName = (round) => {
    const totalRounds = rounds.length;
    const roundNum = parseInt(round);
    if (roundNum === totalRounds) return "Championship";
    if (roundNum === totalRounds - 1) return "Semi-Finals";
    if (roundNum === totalRounds - 2) return "Quarter-Finals";
    return `Round ${roundNum}`;
  };

  const toggleRound = (round) => {
    setExpandedRounds(prev => ({ ...prev, [round]: !prev[round] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Calculate winners for completed games
      const updatedMatchups = matchups.map(m => {
        let winner_id = m.winner_id;
        const t1Score = m.team1_score !== "" ? parseInt(m.team1_score) : null;
        const t2Score = m.team2_score !== "" ? parseInt(m.team2_score) : null;
        
        if (t1Score !== null && t2Score !== null) {
          if (t1Score > t2Score) winner_id = m.team1_id;
          else if (t2Score > t1Score) winner_id = m.team2_id;
        }
        
        return {
          ...m,
          team1_score: t1Score,
          team2_score: t2Score,
          winner_id
        };
      });

      await axios.put(
        `${API}/leagues/${leagueId}/playoffs/${playoff.id}/matchups`,
        { matchups: updatedMatchups },
        { headers: getAuthHeader() }
      );
      
      if (onSave) onSave();
      onClose();
    } catch (err) {
      console.error("Failed to save bracket:", err);
      alert("Failed to save bracket. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Apply same date/time/location to all games in a round
  const applyToRound = (round, field, value) => {
    setMatchups(prev => prev.map(m => 
      m.round === parseInt(round) ? { ...m, [field]: value } : m
    ));
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-4xl my-8">
        {/* Header */}
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <div>
            <h3 className="text-lg font-bold text-white">Bracket Editor</h3>
            <p className="text-sm text-zinc-500">{playoff.name} • {matchups.length} games</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save All
            </Button>
            <button onClick={onClose} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {rounds.map(round => (
            <div key={round} className="bg-zinc-800/50 rounded-lg border border-zinc-700 overflow-hidden">
              {/* Round Header */}
              <button
                onClick={() => toggleRound(round)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-zinc-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-white font-medium">{getRoundName(round)}</span>
                  <span className="text-xs text-zinc-500">({matchupsByRound[round].length} games)</span>
                </div>
                {expandedRounds[round] ? (
                  <ChevronUp className="w-5 h-5 text-zinc-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-zinc-400" />
                )}
              </button>

              {/* Round Content */}
              {expandedRounds[round] && (
                <div className="border-t border-zinc-700">
                  {/* Quick Apply Bar */}
                  <div className="px-4 py-2 bg-zinc-800/30 border-b border-zinc-700 flex flex-wrap items-center gap-3">
                    <span className="text-xs text-zinc-500">Apply to all in round:</span>
                    <input
                      type="date"
                      className="px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-xs text-white"
                      onChange={(e) => applyToRound(round, "game_date", e.target.value)}
                      placeholder="Date"
                    />
                    <input
                      type="time"
                      className="px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-xs text-white"
                      onChange={(e) => applyToRound(round, "game_time", e.target.value)}
                      placeholder="Time"
                    />
                    <input
                      type="text"
                      className="px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-xs text-white w-32"
                      onChange={(e) => applyToRound(round, "location", e.target.value)}
                      placeholder="Location"
                    />
                  </div>

                  {/* Matchups */}
                  <div className="divide-y divide-zinc-700/50">
                    {matchupsByRound[round].map((matchup, idx) => (
                      <div key={matchup.id} className="p-4 hover:bg-zinc-800/20">
                        <div className="flex items-start gap-4">
                          {/* Game Number */}
                          <div className="flex-shrink-0 w-8 h-8 rounded bg-zinc-700 flex items-center justify-center">
                            <span className="text-xs font-bold text-zinc-300">
                              {matchup.position || idx + 1}
                            </span>
                          </div>

                          {/* Teams */}
                          <div className="flex-1 space-y-2">
                            {/* Team 1 */}
                            <div className="flex items-center gap-2">
                              <select
                                value={matchup.team1_id || ""}
                                onChange={(e) => updateMatchup(matchup.id, "team1_id", e.target.value)}
                                className="flex-1 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
                              >
                                <option value="">Select Team 1</option>
                                {teams.map(team => (
                                  <option key={team.id} value={team.id}>
                                    {team.name}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="number"
                                value={matchup.team1_score ?? ""}
                                onChange={(e) => updateMatchup(matchup.id, "team1_score", e.target.value)}
                                className="w-14 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white text-center"
                                placeholder="—"
                                min="0"
                              />
                            </div>

                            {/* Team 2 */}
                            <div className="flex items-center gap-2">
                              <select
                                value={matchup.team2_id || ""}
                                onChange={(e) => updateMatchup(matchup.id, "team2_id", e.target.value)}
                                className="flex-1 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
                              >
                                <option value="">Select Team 2</option>
                                {teams.map(team => (
                                  <option key={team.id} value={team.id}>
                                    {team.name}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="number"
                                value={matchup.team2_score ?? ""}
                                onChange={(e) => updateMatchup(matchup.id, "team2_score", e.target.value)}
                                className="w-14 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white text-center"
                                placeholder="—"
                                min="0"
                              />
                            </div>
                          </div>

                          {/* Date/Time/Location */}
                          <div className="flex-shrink-0 space-y-2 w-64">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-zinc-500" />
                              <input
                                type="date"
                                value={matchup.game_date || ""}
                                onChange={(e) => updateMatchup(matchup.id, "game_date", e.target.value)}
                                className="flex-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-zinc-500" />
                              <input
                                type="time"
                                value={matchup.game_time || ""}
                                onChange={(e) => updateMatchup(matchup.id, "game_time", e.target.value)}
                                className="flex-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-zinc-500" />
                              <input
                                type="text"
                                value={matchup.location || ""}
                                onChange={(e) => updateMatchup(matchup.id, "location", e.target.value)}
                                className="flex-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
                                placeholder="Venue"
                              />
                            </div>
                          </div>

                          {/* Broadcast */}
                          <div className="flex-shrink-0 space-y-2 w-48">
                            <div className="flex items-center gap-2">
                              <Tv className="w-4 h-4 text-zinc-500" />
                              <input
                                type="text"
                                value={matchup.tv_network || ""}
                                onChange={(e) => updateMatchup(matchup.id, "tv_network", e.target.value)}
                                className="flex-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
                                placeholder="TV Network"
                              />
                            </div>
                            <input
                              type="url"
                              value={matchup.youtube_url || ""}
                              onChange={(e) => updateMatchup(matchup.id, "youtube_url", e.target.value)}
                              className="w-full px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
                              placeholder="Stream URL"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 flex justify-end gap-3 sticky bottom-0 bg-zinc-900">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Bracket
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BracketEditorModal;
